/**
 * Level-up celebration — rising medal, star glitter, soft fanfare.
 * Canvas overlay used by LevelUpCelebration (same quality bar as musical task-complete).
 */

const RISE_MS = 1600
const SETTLE_MS = 2000
export const LEVEL_UP_CELEBRATION_MS = RISE_MS + SETTLE_MS

/** Amok gold / green / blue — triumphant, not neon-purple */
const COLORS = [
  "#F5C15A",
  "#E8A838",
  "#FFE9B0",
  "#46A631",
  "#228773",
  "#6391BA",
  "#DA6223",
  "#FFFFFF",
]

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches

let audioCtx = null

const getAudioContext = () => {
  if (typeof window === "undefined") return null
  const AC = window.AudioContext || window.webkitAudioContext
  if (!AC) return null
  if (!audioCtx) audioCtx = new AC()
  if (audioCtx.state === "suspended") {
    audioCtx.resume().catch(() => {})
  }
  return audioCtx
}

const playTone = (
  ctx,
  { freq, when, duration = 0.5, type = "sine", volume = 0.07 },
) => {
  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  const filter = ctx.createBiquadFilter()

  osc.type = type
  osc.frequency.setValueAtTime(freq, when)

  filter.type = "lowpass"
  filter.frequency.setValueAtTime(2800, when)

  gain.gain.setValueAtTime(0.0001, when)
  gain.gain.exponentialRampToValueAtTime(volume, when + 0.04)
  gain.gain.exponentialRampToValueAtTime(0.0001, when + duration)

  osc.connect(filter)
  filter.connect(gain)
  gain.connect(ctx.destination)

  osc.start(when)
  osc.stop(when + duration + 0.05)
}

/**
 * Soft ascending fanfare — call from the same gesture that triggers the overlay.
 */
export const playLevelUpCelebrationTone = () => {
  if (typeof window === "undefined") return
  if (prefersReducedMotion()) return
  // Onboarding never plays level-up audio (celebration is deferred to dashboard).
  if (
    typeof window !== "undefined" &&
    window.location?.pathname &&
    (window.location.pathname === "/onboarding" ||
      window.location.pathname.startsWith("/onboarding/"))
  ) {
    return
  }

  try {
    const ctx = getAudioContext()
    if (!ctx) return

    const t0 = ctx.currentTime + 0.02

    // Rising major triad → octave (triumphant, soft)
    const notes = [
      { freq: 392.0, at: 0, dur: 0.5, vol: 0.055 }, // G4
      { freq: 493.88, at: 0.12, dur: 0.55, vol: 0.05 }, // B4
      { freq: 587.33, at: 0.24, dur: 0.6, vol: 0.048 }, // D5
      { freq: 784.0, at: 0.4, dur: 0.85, vol: 0.052 }, // G5
    ]
    notes.forEach(n => {
      playTone(ctx, {
        freq: n.freq,
        when: t0 + n.at,
        duration: n.dur,
        type: "sine",
        volume: n.vol,
      })
      playTone(ctx, {
        freq: n.freq,
        when: t0 + n.at,
        duration: n.dur * 0.85,
        type: "triangle",
        volume: n.vol * 0.32,
      })
    })

    // Soft resolve chord when medal lands
    const landAt = t0 + RISE_MS * 0.001 * 0.72
    ;[523.25, 659.25, 783.99, 1046.5].forEach((freq, i) => {
      playTone(ctx, {
        freq,
        when: landAt + i * 0.028,
        duration: 0.9,
        type: i % 2 === 0 ? "sine" : "triangle",
        volume: 0.038,
      })
    })
  } catch {
    // Autoplay / AudioContext blocked — fail silently
  }
}

const easeOutCubic = t => 1 - (1 - t) ** 3
const easeOutBack = t => {
  const c1 = 1.70158
  const c3 = c1 + 1
  return 1 + c3 * (t - 1) ** 3 + c1 * (t - 1) ** 2
}

const drawStar = (ctx, x, y, outer, inner, points = 5) => {
  const step = Math.PI / points
  ctx.beginPath()
  for (let i = 0; i < points * 2; i += 1) {
    const r = i % 2 === 0 ? outer : inner
    const a = i * step - Math.PI / 2
    const fn = i === 0 ? ctx.moveTo : ctx.lineTo
    fn.call(ctx, x + Math.cos(a) * r, y + Math.sin(a) * r)
  }
  ctx.closePath()
}

/** Gold medal with ribbon — drawn facing camera */
const drawMedal = (ctx, x, y, scale, pulse, opacity) => {
  ctx.save()
  ctx.translate(x, y)
  ctx.scale(scale * (1 + pulse * 0.04), scale * (1 - pulse * 0.02))
  ctx.globalAlpha = opacity
  ctx.lineCap = "round"
  ctx.lineJoin = "round"

  // Soft aura
  const glow = ctx.createRadialGradient(0, 0, 6, 0, 0, 58)
  glow.addColorStop(0, "rgba(245, 193, 90, 0.55)")
  glow.addColorStop(0.45, "rgba(70, 166, 49, 0.12)")
  glow.addColorStop(1, "rgba(245, 193, 90, 0)")
  ctx.fillStyle = glow
  ctx.beginPath()
  ctx.arc(0, 0, 58, 0, Math.PI * 2)
  ctx.fill()

  // Ribbon
  ctx.fillStyle = "#228773"
  ctx.beginPath()
  ctx.moveTo(-10, -28)
  ctx.lineTo(-18, -52)
  ctx.lineTo(-6, -48)
  ctx.lineTo(0, -30)
  ctx.closePath()
  ctx.fill()
  ctx.beginPath()
  ctx.moveTo(10, -28)
  ctx.lineTo(18, -52)
  ctx.lineTo(6, -48)
  ctx.lineTo(0, -30)
  ctx.closePath()
  ctx.fill()
  ctx.fillStyle = "#46A631"
  ctx.beginPath()
  ctx.moveTo(-8, -30)
  ctx.lineTo(8, -30)
  ctx.lineTo(6, -46)
  ctx.lineTo(-6, -46)
  ctx.closePath()
  ctx.fill()

  // Medal disc
  const disc = ctx.createLinearGradient(-22, -18, 22, 26)
  disc.addColorStop(0, "#FFE9B0")
  disc.addColorStop(0.35, "#F5C15A")
  disc.addColorStop(0.7, "#E8A838")
  disc.addColorStop(1, "#C47A2C")
  ctx.beginPath()
  ctx.arc(0, 4, 22, 0, Math.PI * 2)
  ctx.fillStyle = disc
  ctx.fill()
  ctx.strokeStyle = "rgba(120, 70, 20, 0.35)"
  ctx.lineWidth = 1.5
  ctx.stroke()

  // Inner ring
  ctx.beginPath()
  ctx.arc(0, 4, 16, 0, Math.PI * 2)
  ctx.strokeStyle = "rgba(255, 233, 176, 0.75)"
  ctx.lineWidth = 2
  ctx.stroke()

  // Center star
  ctx.fillStyle = "#FFF8E7"
  drawStar(ctx, 0, 4, 9, 3.8, 5)
  ctx.fill()
  ctx.fillStyle = "#E8A838"
  drawStar(ctx, 0, 4, 5.5, 2.2, 5)
  ctx.fill()

  // Highlight
  ctx.strokeStyle = "rgba(255, 255, 255, 0.45)"
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.arc(-6, -2, 10, -Math.PI * 0.9, -Math.PI * 0.2)
  ctx.stroke()

  ctx.restore()
}

/**
 * Run the level-up celebration on a full-viewport canvas.
 * Returns a cancel function.
 */
export const runLevelUpCelebration = (canvas, { onComplete } = {}) => {
  if (!canvas) return () => {}

  const ctx = canvas.getContext("2d")
  if (!ctx) return () => {}

  let raf = 0
  let cancelled = false

  const resize = () => {
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const w = window.innerWidth
    const h = window.innerHeight
    canvas.width = Math.floor(w * dpr)
    canvas.height = Math.floor(h * dpr)
    canvas.style.width = `${w}px`
    canvas.style.height = `${h}px`
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  }

  resize()
  window.addEventListener("resize", resize)

  if (prefersReducedMotion()) {
    const timer = window.setTimeout(() => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      onComplete?.()
    }, 400)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
      window.removeEventListener("resize", resize)
    }
  }

  const W = () => window.innerWidth
  const H = () => window.innerHeight

  const trail = []
  const glitter = []
  const sparks = []
  const rays = []
  let hasBurst = false
  let start = null

  for (let i = 0; i < 10; i += 1) {
    rays.push({
      angle: (i / 10) * Math.PI * 2 + Math.random() * 0.2,
      length: 80 + Math.random() * 120,
      width: 2 + Math.random() * 3,
      color: COLORS[i % 5],
      opacity: 0,
    })
  }

  const emitTrail = (x, y) => {
    for (let i = 0; i < 2; i += 1) {
      trail.push({
        x: x + (Math.random() - 0.5) * 18,
        y: y + 12 + Math.random() * 10,
        vx: (Math.random() - 0.5) * 1.2,
        vy: 0.6 + Math.random() * 1.4,
        size: 0.4 + Math.random() * 0.5,
        color: COLORS[Math.floor(Math.random() * 4)],
        life: 1,
        decay: 0.02 + Math.random() * 0.02,
        rot: Math.random() * Math.PI,
        vRot: (Math.random() - 0.5) * 0.1,
        kind: Math.random() > 0.45 ? "star" : "dot",
      })
    }
  }

  const createBurst = (bx, by) => {
    for (let i = 0; i < 64; i += 1) {
      const a = Math.random() * Math.PI * 2
      const speed = 2.2 + Math.random() * 10
      glitter.push({
        x: bx,
        y: by,
        vx: Math.cos(a) * speed,
        vy: Math.sin(a) * speed - 1.8,
        size: 0.45 + Math.random() * 0.75,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        rot: Math.random() * Math.PI,
        vRot: (Math.random() - 0.5) * 0.22,
        opacity: 1,
        gravity: 0.15,
        drag: 0.97,
        kind: Math.random() > 0.35 ? "star" : "dot",
      })
    }
    for (let i = 0; i < 28; i += 1) {
      const a = Math.random() * Math.PI * 2
      const speed = 1.4 + Math.random() * 7
      sparks.push({
        x: bx,
        y: by,
        vx: Math.cos(a) * speed,
        vy: Math.sin(a) * speed,
        size: 5 + Math.random() * 14,
        color: COLORS[Math.floor(Math.random() * 5)],
        opacity: 1,
        decay: 0.01 + Math.random() * 0.016,
      })
    }
  }

  const frame = now => {
    if (cancelled) return
    if (start == null) start = now

    const w = W()
    const h = H()
    const elapsed = now - start
    const riseT = Math.min(elapsed / RISE_MS, 1)
    const rise = easeOutCubic(riseT)
    const landPop = riseT > 0.55 ? easeOutBack(Math.min((riseT - 0.55) / 0.45, 1)) : 0

    ctx.clearRect(0, 0, w, h)

    const cx = w * 0.5
    const startY = h * 0.92
    const endY = h * 0.38
    const my = riseT < 1 ? startY + (endY - startY) * rise : endY
    const pulse = Math.sin(elapsed * 0.014)

    // Soft vignette while rising
    if (riseT < 1 || glitter.length > 0) {
      const vignette = ctx.createRadialGradient(
        cx,
        endY,
        40,
        cx,
        endY,
        Math.max(w, h) * 0.55,
      )
      vignette.addColorStop(0, "rgba(245, 193, 90, 0.08)")
      vignette.addColorStop(1, "rgba(0, 0, 0, 0)")
      ctx.fillStyle = vignette
      ctx.fillRect(0, 0, w, h)
    }

    // Rays expand when medal lands
    if (riseT > 0.55) {
      const rayFade =
        elapsed > RISE_MS + SETTLE_MS * 0.7
          ? Math.max(0, 1 - (elapsed - RISE_MS - SETTLE_MS * 0.7) / (SETTLE_MS * 0.3))
          : 1
      const rayAlpha =
        Math.min(1, (riseT - 0.55) / 0.25) * rayFade
      rays.forEach(ray => {
        ctx.save()
        ctx.translate(cx, endY)
        ctx.rotate(ray.angle + elapsed * 0.00015)
        ctx.globalAlpha = rayAlpha * 0.35
        const grad = ctx.createLinearGradient(0, 0, ray.length, 0)
        grad.addColorStop(0, ray.color)
        grad.addColorStop(1, "rgba(255,255,255,0)")
        ctx.strokeStyle = grad
        ctx.lineWidth = ray.width
        ctx.beginPath()
        ctx.moveTo(18, 0)
        ctx.lineTo(ray.length * (0.7 + landPop * 0.3), 0)
        ctx.stroke()
        ctx.restore()
      })
    }

    if (riseT < 1) {
      emitTrail(cx, my)
    }

    for (let i = trail.length - 1; i >= 0; i -= 1) {
      const p = trail[i]
      p.x += p.vx
      p.y += p.vy
      p.rot += p.vRot
      p.life -= p.decay
      if (p.life <= 0) {
        trail.splice(i, 1)
        continue
      }
      ctx.save()
      ctx.globalAlpha = p.life * 0.9
      ctx.fillStyle = p.color
      ctx.translate(p.x, p.y)
      ctx.rotate(p.rot)
      if (p.kind === "star") {
        drawStar(ctx, 0, 0, 6 * p.size, 2.4 * p.size)
        ctx.fill()
      } else {
        ctx.beginPath()
        ctx.arc(0, 0, 2.4 * p.size, 0, Math.PI * 2)
        ctx.fill()
      }
      ctx.restore()
    }

    // Medal stays centered after landing (fade only at end of settle)
    let medalOpacity = 1
    if (riseT < 0.08) medalOpacity = riseT / 0.08
    if (elapsed > RISE_MS + SETTLE_MS * 0.72) {
      medalOpacity = Math.max(
        0,
        1 - (elapsed - RISE_MS - SETTLE_MS * 0.72) / (SETTLE_MS * 0.28),
      )
    }

    const medalScale =
      Math.min(1.25, Math.max(0.9, w / 1200)) * (0.9 + landPop * 0.18)
    drawMedal(ctx, cx, my, medalScale, pulse, medalOpacity)

    if (riseT >= 0.7 && !hasBurst) {
      hasBurst = true
      createBurst(cx, endY)
    } else if (riseT >= 1 && !hasBurst) {
      hasBurst = true
      createBurst(cx, endY)
    }

    for (let i = glitter.length - 1; i >= 0; i -= 1) {
      const c = glitter[i]
      c.vx *= c.drag
      c.vy *= c.drag
      c.vy += c.gravity
      c.x += c.vx
      c.y += c.vy
      c.rot += c.vRot
      c.opacity -= 0.0055
      if (c.opacity <= 0 || c.y > h + 40) {
        glitter.splice(i, 1)
        continue
      }
      ctx.save()
      ctx.globalAlpha = Math.max(0, c.opacity)
      ctx.fillStyle = c.color
      ctx.translate(c.x, c.y)
      ctx.rotate(c.rot)
      ctx.shadowColor = c.color
      ctx.shadowBlur = 8
      if (c.kind === "star") {
        drawStar(ctx, 0, 0, 7 * c.size, 2.8 * c.size)
        ctx.fill()
      } else {
        ctx.beginPath()
        ctx.arc(0, 0, 2.8 * c.size, 0, Math.PI * 2)
        ctx.fill()
      }
      ctx.restore()
    }

    for (let i = sparks.length - 1; i >= 0; i -= 1) {
      const s = sparks[i]
      s.x += s.vx
      s.y += s.vy
      s.vx *= 0.98
      s.vy *= 0.98
      s.opacity -= s.decay
      if (s.opacity <= 0) {
        sparks.splice(i, 1)
        continue
      }
      ctx.save()
      ctx.globalAlpha = Math.max(0, s.opacity)
      ctx.fillStyle = s.color
      ctx.shadowColor = s.color
      ctx.shadowBlur = 12
      drawStar(ctx, s.x, s.y, s.size, s.size * 0.38)
      ctx.fill()
      ctx.restore()
    }

    const stillAlive =
      riseT < 1 ||
      glitter.length > 0 ||
      sparks.length > 0 ||
      trail.length > 0 ||
      elapsed < RISE_MS + SETTLE_MS

    if (stillAlive) {
      raf = requestAnimationFrame(frame)
    } else {
      ctx.clearRect(0, 0, w, h)
      window.removeEventListener("resize", resize)
      onComplete?.()
    }
  }

  raf = requestAnimationFrame(frame)

  return () => {
    cancelled = true
    cancelAnimationFrame(raf)
    window.removeEventListener("resize", resize)
    ctx.clearRect(0, 0, canvas.width, canvas.height)
  }
}
